import type { AlgoliaSearchHelper } from 'lib/util/algolia';
import { NmdpAppProviderContext } from './NmdpAppProviderContext';
import { NmdpAppContext } from './context';
import { useRef, useState } from 'react';

/**
 * A React functional component that provides the application context for the NMDP app.
 * It initializes and manages the state and context for the application, including the search client.
 *
 * @param {Object} props - The props for the component.
 * @param {React.ReactNode} props.children - The child components to be rendered within the provider.
 *
 * @returns {JSX.Element} A context provider component that wraps its children with the application context.
 */
const NmdpAppProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const [searchState, setSearchState] = useState<AlgoliaSearchHelper | null>(null);
  const appContext = useRef<NmdpAppProviderContext>(
    new NmdpAppProviderContext({ searchHelperTuple: [searchState, setSearchState] })
  );
  appContext.current.setSearchHelper = setSearchState;
  appContext.current.searchHelper = searchState;

  return <NmdpAppContext.Provider value={appContext.current}>{children}</NmdpAppContext.Provider>;
};

export default NmdpAppProvider;
